import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import shortid from 'shortid';
import Loader from '@rsos/components/capstone/Loader';
import { validatedPayload } from '@rsos/ellington';
import { normalizeInlineRADE } from '@rsos/utils/normalizeRADE';
import fetchEmergencySession from '../../../actions/fetchEmergencySession';
import type { RootState } from '../../../app/rootReducer';
import { getProductToken } from '../../../utils/getProductToken';
import { EllingtonCard } from '../../AdditionalData/EllingtonCard/EllingtonCard';
import ErrorBoundary from '../../AdditionalData/EllingtonCard/ErrorBoundary';
import { RADEError } from '../../AdditionalData/RADEErrors';
import type { MafErrorState } from '../UniversalSearch/ConnectedUniversalSearch.types';
import { MAF_ERROR_REQUEST_FAILED } from '../constants';
import {
  CenteredBox,
  SearchPanel,
  SearchHeader,
  SearchBody,
} from './RADECard.styles';

interface RadeCardItem {
  org?: string;
  data?: Record<string, unknown> | null;
  display_schema?: Record<string, unknown> | null;
}

interface FetchDataLinkProps {
  mafid: string;
  schema?: {
    attributes?: Record<string, unknown>;
  };
}

interface ConnectedRADECardProps {
  setMafError: (error: MafErrorState) => void;
  setADRError: (error: boolean) => void;
}

interface RADECardProps {
  loading: boolean;
  error: boolean;
  data: RadeCardItem[] | null;
  accessToken: string | null;
  onFetchDataLink: (props: FetchDataLinkProps) => Promise<RadeCardItem[]>;
}

interface EllingtonCardProps {
  data: Record<string, unknown>;
  display_schema: Record<string, unknown>;
  createdTime: number;
  onFetchDataLink: (props: FetchDataLinkProps) => Promise<RadeCardItem[]>;
  showDisclaimer: boolean;
  source?: string;
  showExitButton: boolean;
  accessToken: string | null;
}

type EmergencyDataDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

const EllingtonCardComponent =
  EllingtonCard as unknown as React.ComponentType<EllingtonCardProps>;

export const RADECard = React.memo(
  ({ loading, error, data, accessToken, onFetchDataLink }: RADECardProps) => {
    if (!data || data.length === 0) return null;
    return (
      <SearchPanel>
        <SearchHeader />
        {loading ? (
          <SearchBody>
            <CenteredBox>
              <Loader />
            </CenteredBox>
          </SearchBody>
        ) : error ? (
          <SearchBody>
            <CenteredBox>
              <RADEError />
            </CenteredBox>
          </SearchBody>
        ) : (
          <ErrorBoundary>
            {data.map(child => {
              const validated = validatedPayload({
                data: child.data || {},
                display_schema: child.display_schema || {},
              });
              return (
                <EllingtonCardComponent
                  key={shortid.generate()}
                  data={validated.data}
                  display_schema={validated.display_schema}
                  createdTime={Date.now()}
                  onFetchDataLink={onFetchDataLink}
                  showDisclaimer
                  source={child.org}
                  showExitButton
                  accessToken={accessToken}
                />
              );
            })}
          </ErrorBoundary>
        )}
      </SearchPanel>
    );
  },
);

const ConnectedRADECard = ({
  setMafError,
  setADRError,
}: ConnectedRADECardProps) => {
  const dispatch = useDispatch();
  const asyncDispatch = dispatch as unknown as EmergencyDataDispatch;

  const [data, setData] = useState<RadeCardItem[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const showing = useSelector((state: RootState) => state.rade.showing);
  const triggerSearch = useSelector(
    (state: RootState) => state.rade.triggerSearch,
  );
  // TODO(near-term): stop reading from the legacy universal-search
  // slice. The GlobalMapSearch path writes a fake `/maf <id>` token
  // into `state.search.searchTerm` purely so this effect can
  // string-sniff it back out below. When `ConnectedUniversalSearch`
  // is retired we should add an explicit field to `radeSlice` (e.g.
  // `mafLookup: { id } | null`), have `useMedicAlertSearch.search`
  // dispatch that, and replace the `searchTerm` read + the
  // `/maf` / `///` parsing with a direct lookup + hardcoded
  // `scheme: 'maf-id'`. See the matching TODO in
  // `useMedicAlertSearch.ts`.
  const searchTerm = useSelector((state: RootState) => state.search.searchTerm);
  const { productToken = '' } = useSelector((state: RootState) =>
    getProductToken(state.sinatra),
  ) as { productToken?: string };

  const handleFetchDataLink = useCallback(
    async ({ mafid, schema = {} }: FetchDataLinkProps) => {
      const { attributes = {} } = schema;
      const { payload } = await asyncDispatch(
        fetchEmergencySession(mafid, {
          attributes,
          silentUpdate: true,
        }),
      );
      return normalizeInlineRADE(payload);
    },
    [asyncDispatch],
  );

  useEffect(() => {
    if (showing) {
      (async () => {
        setError(false);
        setLoading(true);
        let scheme: string | undefined;
        try {
          if (!searchTerm) {
            return;
          }
          // Strip slashes, spaces, and dashes — searchTerm may be a phone number, and we need digits only
          let normalizedSearchTerm = searchTerm.replace(/\/|\s|-/g, '');
          if (searchTerm.includes('/maf')) {
            scheme = 'maf-id';
            normalizedSearchTerm = normalizedSearchTerm.replace('maf', '');
          } else if (!searchTerm.includes('///')) {
            scheme = 'tel';
          }
          const { payload } = await asyncDispatch(
            fetchEmergencySession(normalizedSearchTerm, {
              attributes: { scheme },
            }),
          );
          const normalized = normalizeInlineRADE(payload || {});

          if (!normalized.length) {
            if (scheme === 'maf-id') {
              setMafError(true);
            }
            if (scheme === 'tel') {
              setADRError(true);
            }
          }
          setData(normalized);
        } catch (err) {
          // eslint-disable-next-line no-console -- error visibility for RADE data fetch failures
          console.error(err);
          if (scheme === 'tel') {
            setADRError(true);
          } else {
            setMafError(MAF_ERROR_REQUEST_FAILED);
            setError(true);
          }
          setData([]);
        } finally {
          setLoading(false);
        }
      })();
    }
    return () => {
      setData(null);
      setError(false);
      setLoading(false);
    };
    // NOTE Ignoring searchTerm in deps, so it doesn't retrigger on every key change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showing, triggerSearch]);

  return (
    showing && (
      <RADECard
        data={data}
        error={error}
        loading={loading}
        onFetchDataLink={handleFetchDataLink}
        accessToken={productToken}
      />
    )
  );
};

export default ConnectedRADECard;
